import { useEffect, useRef, useState } from 'react';

import { ApiError, apiRequest } from '../api/client.js';
import type { ModerationRule, ModerationRuleKind, ModerationSeverity } from '../api/types.js';
import { Alert } from './Alert.js';

/**
 * Create or edit a moderation rule (ARCHITECTURE.md §65, §98).
 *
 * A native `<dialog>`, for the same reasons the Discord channel picker is one:
 * focus trapping, Escape, and inertness of the page behind it come from the
 * platform rather than from code that has to be maintained.
 *
 * THE PART THAT MATTERS MOST is the test box. A rule is a sentence about other
 * people's messages, and an operator writing one has no way to know what it
 * will catch until it starts catching things. Being able to paste three real
 * messages and see which match — before saving, and without touching any
 * provider — is what turns rule writing from guesswork into editing.
 *
 * Validation lives on the SERVER. This form does not re-implement the pattern
 * safety check; it submits and shows what came back. Two implementations of
 * "is this regex safe" would eventually disagree, and the one that mattered
 * would be the one this file does not contain.
 */

const KINDS: { value: ModerationRuleKind; label: string; help: string }[] = [
  {
    value: 'blocked_terms',
    label: 'Blocked words',
    help: 'Flags a message containing any of these words. Whole words by default, so "class" does not match "ass".',
  },
  {
    value: 'regex',
    label: 'Pattern',
    help: 'For anything a word list cannot express. Patterns that could run slowly are refused when you save.',
  },
  {
    value: 'links',
    label: 'Links',
    help: 'Flags messages containing links. Add domains you trust to let those through.',
  },
  {
    value: 'repetition',
    label: 'Repeated messages',
    help: 'Flags someone saying the same thing over and over.',
  },
  {
    value: 'flood',
    label: 'Message flood',
    help: 'Flags someone sending too many messages too quickly.',
  },
];

const SEVERITIES: ModerationSeverity[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

type TestResult = { text: string; matched: boolean; reason: string | null };

export type RuleEditorDialogProps = {
  /** Null when creating. */
  rule: ModerationRule | null;
  onClose: () => void;
  onSaved: () => void;
};

/** Turn form state into the discriminated config the API expects. */
function buildConfig(
  kind: ModerationRuleKind,
  form: {
    terms: string;
    allowTerms: string;
    matchMode: 'word' | 'phrase';
    pattern: string;
    allowedDomains: string;
    windowSeconds: number;
    maxRepeats: number;
    maxMessages: number;
  },
): Record<string, unknown> {
  const list = (raw: string): string[] =>
    raw
      .split(/[\n,]/u)
      .map((value) => value.trim())
      .filter((value) => value !== '');

  switch (kind) {
    case 'blocked_terms':
      return {
        kind,
        terms: list(form.terms),
        allowTerms: list(form.allowTerms),
        matchMode: form.matchMode,
        caseSensitive: false,
      };
    case 'regex':
      return { kind, pattern: form.pattern, allowTerms: list(form.allowTerms), caseSensitive: false };
    case 'links':
      return { kind, allowedDomains: list(form.allowedDomains), includeBareDomains: true };
    case 'repetition':
      return {
        kind,
        windowSeconds: form.windowSeconds,
        maxRepeats: form.maxRepeats,
        minLength: 4,
      };
    case 'flood':
      return { kind, windowSeconds: form.windowSeconds, maxMessages: form.maxMessages };
  }
}

export function RuleEditorDialog({
  rule,
  onClose,
  onSaved,
}: RuleEditorDialogProps): React.JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);

  const [name, setName] = useState(rule?.name ?? '');
  const [kind, setKind] = useState<ModerationRuleKind>(rule?.kind ?? 'blocked_terms');
  const [severity, setSeverity] = useState<ModerationSeverity>(rule?.severity ?? 'MEDIUM');
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);

  const config = rule?.config ?? {};
  const asList = (value: unknown): string =>
    Array.isArray(value) ? (value as string[]).join('\n') : '';

  const [terms, setTerms] = useState(asList(config['terms']));
  const [allowTerms, setAllowTerms] = useState(asList(config['allowTerms']));
  const [matchMode, setMatchMode] = useState<'word' | 'phrase'>(
    config['matchMode'] === 'phrase' ? 'phrase' : 'word',
  );
  const [pattern, setPattern] = useState(
    typeof config['pattern'] === 'string' ? config['pattern'] : '',
  );
  const [allowedDomains, setAllowedDomains] = useState(asList(config['allowedDomains']));
  const [windowSeconds, setWindowSeconds] = useState(
    typeof config['windowSeconds'] === 'number' ? config['windowSeconds'] : 60,
  );
  const [maxRepeats, setMaxRepeats] = useState(
    typeof config['maxRepeats'] === 'number' ? config['maxRepeats'] : 3,
  );
  const [maxMessages, setMaxMessages] = useState(
    typeof config['maxMessages'] === 'number' ? config['maxMessages'] : 8,
  );

  const [samples, setSamples] = useState('');
  const [results, setResults] = useState<TestResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // showModal(), not the `open` attribute: only the former traps focus and
    // makes the page behind inert.
    dialogRef.current?.showModal();
  }, []);

  const formState = {
    terms,
    allowTerms,
    matchMode,
    pattern,
    allowedDomains,
    windowSeconds,
    maxRepeats,
    maxMessages,
  };

  const runTest = async (): Promise<void> => {
    setError(null);
    const lines = samples.split('\n').filter((line) => line.trim() !== '');

    if (lines.length === 0) {
      setError('Paste a message or two to test against.');
      return;
    }

    try {
      const data = await apiRequest<{ results: TestResult[] }>('/moderation/rules/test', {
        method: 'POST',
        body: { config: buildConfig(kind, formState), severity, samples: lines },
      });
      setResults(data.results);
    } catch (cause) {
      setResults(null);
      setError(cause instanceof ApiError ? cause.message : 'Connaro could not test that rule.');
    }
  };

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);

    try {
      const body = {
        name,
        enabled,
        severity,
        config: buildConfig(kind, formState),
      };

      if (rule === null) {
        /*
         * A new rule is unscoped: empty `platforms`/`channelIds` mean every
         * platform and every channel (`packages/moderation/src/rules.ts`).
         * The dialog offers no control for either yet, so create says so
         * explicitly rather than leaving the server to guess.
         */
        await apiRequest('/moderation/rules', {
          method: 'POST',
          body: { ...body, platforms: [], channelIds: [] },
        });
      } else {
        /*
         * EDIT SENDS NEITHER. This dialog has no platform or channel control,
         * and an explicit `[]` is a real instruction meaning "every platform"
         * — so sending it wiped the scope of any rule narrowed through the API
         * or a seed, every time somebody renamed it. Omitting them leaves what
         * is stored alone (`rulePatchSchema`). Add them back here the day this
         * dialog can actually edit them.
         */
        await apiRequest(`/moderation/rules/${rule.id}`, { method: 'PATCH', body });
      }
      onSaved();
    } catch (cause) {
      /*
       * The server's own words, including a refused pattern.
       *
       * "This pattern can take exponential time to match" tells an operator
       * what to change; "invalid input" tells them Connaro is broken.
       */
      setError(
        cause instanceof ApiError
          ? (cause.fieldError('config.pattern') ?? cause.message)
          : 'Connaro could not save that rule.',
      );
    } finally {
      setSaving(false);
    }
  };

  const help = KINDS.find((entry) => entry.value === kind)?.help ?? '';

  return (
    <dialog
      ref={dialogRef}
      className="dialog"
      aria-labelledby="rule-editor-heading"
      onClose={onClose}
      onCancel={onClose}
    >
      <form
        method="dialog"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className="panel__header">
          <h2 className="panel__title" id="rule-editor-heading">
            {rule === null ? 'Add a rule' : 'Edit rule'}
          </h2>
        </div>

        <div className="panel__body">
          {error === null ? null : <Alert tone="error">{error}</Alert>}

          <div className="field">
            <label className="field__label" htmlFor="rule-name">
              Name
            </label>
            <input
              id="rule-name"
              className="field__input"
              value={name}
              maxLength={120}
              required
              onChange={(event) => {
                setName(event.target.value);
              }}
            />
            <p className="field__hint">
              You will read this on every finding it produces, so name it after what it catches.
            </p>
          </div>

          <div className="rule-form__row rule-form__row--pair">
            <div className="field">
              <label className="field__label" htmlFor="rule-kind">
                What it looks for
              </label>
              <select
                id="rule-kind"
                className="field__input"
                value={kind}
                onChange={(event) => {
                  setKind(event.target.value as ModerationRuleKind);
                  setResults(null);
                }}
              >
                {KINDS.map((entry) => (
                  <option key={entry.value} value={entry.value}>
                    {entry.label}
                  </option>
                ))}
              </select>
              <p className="field__hint">{help}</p>
            </div>

            <div className="field">
              <label className="field__label" htmlFor="rule-severity">
                Severity
              </label>
              <select
                id="rule-severity"
                className="field__input"
                value={severity}
                onChange={(event) => {
                  setSeverity(event.target.value as ModerationSeverity);
                }}
              >
                {SEVERITIES.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
              <p className="field__hint">
                Sorts your queue. It never decides what happens to anyone.
              </p>
            </div>
          </div>

          {kind === 'blocked_terms' ? (
            <>
              <div className="field">
                <label className="field__label" htmlFor="rule-terms">
                  Words and phrases
                </label>
                <textarea
                  id="rule-terms"
                  className="field__input"
                  rows={4}
                  value={terms}
                  onChange={(event) => {
                    setTerms(event.target.value);
                  }}
                />
                <p className="field__hint">One per line, or separated by commas.</p>
              </div>

              <div className="field">
                <label className="field__label" htmlFor="rule-match-mode">
                  How to match
                </label>
                <select
                  id="rule-match-mode"
                  className="field__input"
                  value={matchMode}
                  onChange={(event) => {
                    setMatchMode(event.target.value === 'phrase' ? 'phrase' : 'word');
                  }}
                >
                  <option value="word">Whole words only</option>
                  <option value="phrase">Anywhere in the message</option>
                </select>
                <p className="field__hint">
                  Whole words avoids the classic problem where a rule blocking a short word also
                  flags every longer word containing it.
                </p>
              </div>
            </>
          ) : null}

          {kind === 'regex' ? (
            <div className="field">
              <label className="field__label" htmlFor="rule-pattern">
                Pattern
              </label>
              <input
                id="rule-pattern"
                className="field__input"
                value={pattern}
                maxLength={200}
                onChange={(event) => {
                  setPattern(event.target.value);
                }}
              />
              <p className="field__hint">
                A regular expression. Backreferences, lookahead and repetition inside a repeated
                group are refused, because those can take a very long time to match.
              </p>
            </div>
          ) : null}

          {kind === 'links' ? (
            <div className="field">
              <label className="field__label" htmlFor="rule-domains">
                Domains to allow
              </label>
              <textarea
                id="rule-domains"
                className="field__input"
                rows={3}
                value={allowedDomains}
                onChange={(event) => {
                  setAllowedDomains(event.target.value);
                }}
              />
              <p className="field__hint">
                Leave empty to flag every link. Subdomains of anything listed are allowed too.
              </p>
            </div>
          ) : null}

          {kind === 'repetition' || kind === 'flood' ? (
            <div className="rule-form__row rule-form__row--pair">
              <div className="field">
                <label className="field__label" htmlFor="rule-window">
                  Within (seconds)
                </label>
                <input
                  id="rule-window"
                  type="number"
                  className="field__input"
                  min={kind === 'flood' ? 1 : 5}
                  max={kind === 'flood' ? 600 : 3600}
                  value={windowSeconds}
                  onChange={(event) => {
                    setWindowSeconds(Number(event.target.value));
                  }}
                />
              </div>
              <div className="field">
                <label className="field__label" htmlFor="rule-count">
                  {kind === 'flood' ? 'Messages before flagging' : 'Repeats before flagging'}
                </label>
                <input
                  id="rule-count"
                  type="number"
                  className="field__input"
                  min={2}
                  max={kind === 'flood' ? 200 : 50}
                  value={kind === 'flood' ? maxMessages : maxRepeats}
                  onChange={(event) => {
                    const value = Number(event.target.value);
                    if (kind === 'flood') setMaxMessages(value);
                    else setMaxRepeats(value);
                  }}
                />
              </div>
            </div>
          ) : null}

          {kind === 'blocked_terms' || kind === 'regex' ? (
            <div className="field">
              <label className="field__label" htmlFor="rule-allow">
                Never flag messages containing
              </label>
              <textarea
                id="rule-allow"
                className="field__input"
                rows={2}
                value={allowTerms}
                onChange={(event) => {
                  setAllowTerms(event.target.value);
                }}
              />
              <p className="field__hint">
                An escape hatch for false positives — a word that legitimately contains a blocked
                one, or a phrase your community uses harmlessly.
              </p>
            </div>
          ) : null}

          <div className="field">
            <label className="field__label">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(event) => {
                  setEnabled(event.target.checked);
                }}
              />{' '}
              Enabled
            </label>
          </div>

          {/* --- try it ---------------------------------------------------- */}
          <div className="rule-test">
            <div className="field">
              <label className="field__label" htmlFor="rule-samples">
                Try it against some messages
              </label>
              <textarea
                id="rule-samples"
                className="field__input"
                rows={3}
                value={samples}
                placeholder="One message per line"
                onChange={(event) => {
                  setSamples(event.target.value);
                }}
              />
              <p className="field__hint">
                Nothing is sent to {`Twitch, YouTube or anywhere else`} — this only runs the rule
                here. Repetition and flood rules need history, so they never match a single test
                message.
              </p>
            </div>

            <button
              type="button"
              className="btn btn--sm"
              onClick={() => {
                void runTest();
              }}
            >
              Test
            </button>

            {results === null ? null : (
              <ul className="detail__list" style={{ marginTop: '0.75rem' }}>
                {results.map((result, index) => (
                  <li key={`${String(index)}:${result.text}`} className="rule-test__result">
                    <span className="rule-test__verdict">
                      {result.matched ? 'Flagged' : 'Allowed'}
                    </span>
                    <span>{result.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="panel__header">
          <button
            type="button"
            className="btn"
            onClick={() => {
              dialogRef.current?.close();
            }}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={saving || name.trim() === ''}>
            {saving ? 'Saving…' : 'Save rule'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
